import { useState } from 'react';
import { useGmbPerformanceTrends, useGmbSearchQueries } from '../../hooks/useGmbOptimization';
import { GBMContentWrapper } from '../governance/shared/GMBLoadingState';
import GBMStatCard from '../governance/shared/GBMStatCard';
import GMBEmptyState from '../governance/shared/GMBEmptyState';
import type { GmbPerformanceTrendsResponse, GmbSearchQueriesResponse } from '../../types';

interface Props {
  darkMode?: boolean;
  onNavigate?: (section: string) => void;
}

const chartSeries = [
  { key: 'impressions', label: 'Impressions', color: '#0d9488' },
  { key: 'clicks', label: 'Clicks', color: '#059669' },
  { key: 'calls', label: 'Calls', color: '#d97706' },
  { key: 'directions', label: 'Directions', color: '#7c3aed' },
  { key: 'messages', label: 'Messages', color: '#2563eb' },
  { key: 'profile_views', label: 'Profile Views', color: '#0891b2' },
  { key: 'reviews', label: 'Reviews', color: '#dc2626' },
  { key: 'avg_rating', label: 'Avg Rating', color: '#be185d' },
];

const metricLabels: Record<string, string> = {
  profile_impressions: 'Profile Impressions',
  impressions: 'Impressions',
  website_clicks: 'Website Clicks',
  clicks: 'Clicks',
  calls: 'Calls',
  direction_requests: 'Directions',
  directions: 'Directions',
  messages: 'Messages',
  messages_whatsapp: 'WhatsApp Messages',
  profile_views: 'Profile Views',
  reviews: 'Reviews',
  review_growth: 'Review Growth',
  avg_rating: 'Avg Rating',
  avg_rating_trend: 'Rating Trend',
};

function fmtNumber(value: number | null | undefined) {
  if (value == null) return '—';
  if (Math.abs(value) < 10 && value % 1 !== 0) return value.toFixed(1);
  return value.toLocaleString();
}

export default function SearchPerformanceTrends({ darkMode, onNavigate }: Props) {
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('weekly');
  const trends = useGmbPerformanceTrends(period);
  const queries = useGmbSearchQueries();
  const isLoading = trends.isLoading || queries.isLoading;
  const error = trends.error || queries.error;
  const refetch = () => {
    trends.refetch();
    queries.refetch();
  };

  return (
    <GBMContentWrapper error={error} onRetry={refetch} loading={isLoading}>
      <Inner
        darkMode={darkMode}
        onNavigate={onNavigate}
        period={period}
        setPeriod={setPeriod}
        trends={(trends.data || {}) as GmbPerformanceTrendsResponse}
        queries={(queries.data || {}) as GmbSearchQueriesResponse}
      />
    </GBMContentWrapper>
  );
}

function Inner({
  period,
  setPeriod,
  trends,
  queries,
}: Props & {
  period: 'weekly' | 'monthly';
  setPeriod: (period: 'weekly' | 'monthly') => void;
  trends: GmbPerformanceTrendsResponse;
  queries: GmbSearchQueriesResponse;
}) {
  const chartData = trends.time_series || [];
  const summary = queries.summary || {
    top_performing_count: 0,
    new_queries_count: 0,
    high_opportunity_gaps: 0,
    low_conversion_count: 0,
  };

  const chartWidth = 900;
  const chartHeight = 300;
  const padding = { top: 20, right: 50, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const allValues = chartData.flatMap((d) => chartSeries.map((s) => Number((d as any)[s.key] || 0)));
  const maxVal = Math.max(...allValues, 100) * 1.1;
  const getX = (index: number) => padding.left + (chartData.length <= 1 ? 0 : (index / (chartData.length - 1)) * innerWidth);
  const getY = (val: number) => padding.top + innerHeight - (val / maxVal) * innerHeight;
  const buildPath = (key: string) =>
    chartData
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(Number((d as any)[key] || 0))}`)
      .join(' ');

  if (!trends.kpis?.length && !chartData.length && !queries.top_performing?.length) {
    return <GMBEmptyState title="No optimization data yet" message="Run a GMB sync to populate Phase 5 optimization analytics." icon="ri-line-chart-line" />;
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Performance Trends</h2>
          <div className="flex gap-2">
            {(['weekly', 'monthly'] as const).map((option) => (
              <button
                key={option}
                onClick={() => setPeriod(option)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                  period === option ? 'bg-teal-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {(trends.kpis || []).slice(0, 8).map((kpi) => (
            <GBMStatCard
              key={kpi.metric}
              label={metricLabels[kpi.metric] || kpi.metric.replace(/_/g, ' ')}
              value={fmtNumber(kpi.current)}
              change={`${kpi.trend === 'up' ? '↑' : kpi.trend === 'down' ? '↓' : '→'} ${kpi.change_pct ?? 0}%`}
              color={kpi.trend === 'down' ? 'red' : kpi.trend === 'up' ? 'green' : 'blue'}
              icon="ri-line-chart-line"
            />
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Performance Over Time</h3>
          <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full" style={{ minWidth: '500px' }}>
              {Array.from({ length: 5 }).map((_, i) => {
                const y = padding.top + (i / 4) * innerHeight;
                const val = Math.round(maxVal - (i / 4) * maxVal);
                return (
                  <g key={i}>
                    <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e5e7eb" strokeDasharray="4" />
                    <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{val}</text>
                  </g>
                );
              })}
              {chartData.map((d, i) => (
                <text key={d.period_label} x={getX(i)} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#6b7280">{d.period_label}</text>
              ))}
              {chartSeries.map((s) => (
                <path key={s.key} d={buildPath(s.key)} fill="none" stroke={s.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              ))}
            </svg>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Search Query Insights</h2>
        {summary.top_performing_count <= 3 && (
          <div className="mb-4 flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
            <i className="ri-information-line text-blue-600 mt-0.5"></i>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Showing brand keywords from Google Business Profile. Connect Google Search Console for full search query data including local keywords, CTR, and conversion metrics.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <GBMStatCard label="Top keywords" value={summary.top_performing_count} change="Performing" color="blue" icon="ri-search-line" />
          <GBMStatCard label="New queries" value={summary.new_queries_count} change="Detected" color="green" icon="ri-sparkling-line" />
          <GBMStatCard label="High-opportunity gaps" value={summary.high_opportunity_gaps} change="Actionable" color="orange" icon="ri-lightbulb-line" />
          <GBMStatCard label="Low-conversion" value={summary.low_conversion_count} change="Needs review" color="red" icon="ri-filter-3-line" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <QueryTable title="Top Performing Local Keywords" rows={(queries.top_performing || []).map((r) => [r.keyword, r.impressions, `${r.ctr}%`, `${r.trend}%`])} headers={['Keyword', 'Impressions', 'CTR (est.)', 'Trend (est.)']} />
          <QueryTable title="Low-Conversion Keywords" rows={(queries.low_conversion || []).map((r) => [r.keyword, r.impressions, r.clicks, `${r.conversion_pct}%`])} headers={['Keyword', 'Impressions', 'Clicks', 'Conv.']} />
          <QueryTable title="New Search Queries Detected" rows={(queries.new_queries || []).map((r) => [r.keyword, r.first_seen_label, r.impressions])} headers={['Keyword', 'First Seen', 'Impressions']} />
          <QueryTable title="Keyword Gaps & Opportunities" rows={(queries.keyword_gaps || []).map((r) => [r.search_term, r.in_description ? 'Yes' : 'No', r.in_posts ? 'Yes' : 'No', r.opportunity])} headers={['Search Term', 'In Desc', 'In Posts', 'Opportunity']} />
        </div>
      </div>
    </div>
  );
}

function QueryTable({ title, headers, rows }: { title: string; headers: string[]; rows: Array<Array<string | number>> }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-700">
              {headers.map((header) => (
                <th key={header} className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td className="px-4 py-4 text-xs text-gray-500" colSpan={headers.length}>No data available</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
